/* ==========================================================================
   Admin Auth Context
   Task F20: Provides admin authentication state separate from provider auth
   ========================================================================== */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'
import {
  adminLogin as apiAdminLogin,
  adminLoginTwoFactor as apiAdminLoginTwoFactor,
  adminLogout as apiAdminLogout,
  getAdminToken,
  setAdminToken,
  setAdminTokenExpiry,
  isAdminTokenExpired,
  getAdminRememberMe,
  getPharmacy,
} from '@/api/admin'
import { isTwoFactorChallenge } from '@/api/types'
import type {
  LoginRequest,
  LoginResponse,
  PendingTwoFactorLogin,
  PharmacyUserResponse,
  PharmacyResponse,
} from '@/api/types'
import { decodeJwtPayload, ADMIN_PHARMACY_SLUG_KEY } from '@/utils/portalSlug'
import { safeSetItem } from '@/utils/safeStorage'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface AdminAuthState {
  /** The authenticated admin user, null if not authenticated */
  user: PharmacyUserResponse | null
  /** The pharmacy the admin belongs to */
  pharmacy: PharmacyResponse | null
  /** Whether authentication is currently being checked */
  loading: boolean
  /** Whether the admin is authenticated */
  isAuthenticated: boolean
  /** Error message from the last auth operation */
  error: string | null
  /** Set when the password was right but an authenticator code is still needed
   *  (PORTAL-187). The login page then shows the code step. */
  twoFactorPending: PendingTwoFactorLogin | null
}

type AdminLoginResult = { success: boolean; pharmacySlug?: string; error?: string }

interface AdminAuthContextValue extends AdminAuthState {
  /**
   * Login with email and password.
   *
   * On failure the reason is returned as well as stored in `error`: a caller that
   * reads it right after `await login(...)` would otherwise see the stale value
   * captured by its closure at render time.
   */
  login: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<AdminLoginResult>
  /** Finish a pending login with the authenticator code. */
  verifyTwoFactor: (code: string, rememberDevice: boolean) => Promise<AdminLoginResult>
  /** Abandon a pending login and return to the password form. */
  cancelTwoFactor: () => void
  /** Logout the current admin */
  logout: () => Promise<void>
  /** Refresh the pharmacy data from the API */
  refreshPharmacy: () => Promise<void>
  /** Clear any auth error */
  clearError: () => void
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

export interface AdminAuthProviderProps {
  children: ReactNode
  /** The pharmacy slug to validate against */
  pharmacySlug: string
}

/* --------------------------------------------------------------------------
   Helper: Decode JWT to extract pharmacy info
   -------------------------------------------------------------------------- */

interface JWTPayload {
  sub: string // user_id
  pharmacy_id: string
  role?: string
  email: string
  name: string
  exp: number
}

/**
 * Decode an admin token, treating one issued before roles shipped as unusable.
 *
 * Such a token carries no `role` claim, which would leave the session without a
 * role and silently hide admin-only actions. Returning null routes it through
 * the existing invalid-token path, so the user re-logins once and gets a token
 * with the claim.
 */
function decodeAdminJWT(token: string): (JWTPayload & { role: string }) | null {
  const payload = decodeJwtPayload<JWTPayload>(token)
  if (!payload?.role) return null
  return payload as JWTPayload & { role: string }
}

/* --------------------------------------------------------------------------
   Provider Component
   -------------------------------------------------------------------------- */

/**
 * Admin auth provider that manages admin authentication state.
 * Separate from provider auth - uses different token storage.
 *
 * @example
 * ```tsx
 * <AdminAuthProvider pharmacySlug={slug}>
 *   <AdminDashboard />
 * </AdminAuthProvider>
 * ```
 */
export function AdminAuthProvider({
  children,
  pharmacySlug,
}: AdminAuthProviderProps) {
  const [user, setUser] = useState<PharmacyUserResponse | null>(null)
  const [pharmacy, setPharmacy] = useState<PharmacyResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [twoFactorPending, setTwoFactorPending] =
    useState<PendingTwoFactorLogin | null>(null)

  // Derived state
  const isAuthenticated = !!user

  /**
   * Check for existing session on mount.
   */
  useEffect(() => {
    async function checkAuth() {
      // Check for ?session= URL param (used by internal "Open Admin Portal")
      const params = new URLSearchParams(window.location.search)
      const sessionToken = params.get('session')
      if (sessionToken) {
        // Store the token under this portal so the normal flow picks it up
        setAdminToken(sessionToken, pharmacySlug)
        const payload = decodeJwtPayload<JWTPayload>(sessionToken)
        if (payload?.exp) {
          setAdminTokenExpiry(
            new Date(payload.exp * 1000).toISOString(),
            pharmacySlug
          )
        }
        // Clean the URL
        params.delete('session')
        const clean = params.toString()
        const newUrl = window.location.pathname + (clean ? `?${clean}` : '')
        window.history.replaceState({}, '', newUrl)
      }

      // Sessions are stored per portal slug (PORTAL-174), so read this portal's.
      const token = getAdminToken(pharmacySlug)

      // No token stored
      if (!token) {
        setLoading(false)
        return
      }

      // Token is expired
      if (isAdminTokenExpired(pharmacySlug)) {
        // If "remember me" was not checked, clear the expired token
        if (!getAdminRememberMe(pharmacySlug)) {
          await apiAdminLogout(pharmacySlug)
        }
        setLoading(false)
        return
      }

      // Token exists and not expired - decode and validate
      try {
        const payload = decodeAdminJWT(token)
        if (!payload) {
          await apiAdminLogout(pharmacySlug)
          setLoading(false)
          return
        }

        // Construct user from JWT payload
        const adminUser: PharmacyUserResponse = {
          id: payload.sub,
          pharmacy_id: payload.pharmacy_id,
          email: payload.email,
          name: payload.name,
          role: payload.role as PharmacyUserResponse['role'],
          status: 'active',
          last_login_at: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }

        // Fetch pharmacy data to validate slug and get full details
        const pharmacyData = await getPharmacy(pharmacySlug)

        // Validate that the user's pharmacy matches the current slug
        if (pharmacyData.id !== payload.pharmacy_id) {
          // User is logged into a different pharmacy
          setUser(null)
          setPharmacy(null)
          setLoading(false)
          return
        }

        setUser(adminUser)
        setPharmacy(pharmacyData)

        // Store pharmacy slug for use in landing page
        safeSetItem(ADMIN_PHARMACY_SLUG_KEY, pharmacyData.portal_slug)
      } catch {
        // Token is invalid or pharmacy fetch failed - clear it
        await apiAdminLogout(pharmacySlug)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [pharmacySlug])

  /**
   * Turn a token response into a signed-in admin: decode the JWT for the user
   * and check the account belongs to this portal's pharmacy. Shared by the
   * password login and the 2FA code step (PORTAL-187). Errors propagate to the
   * caller's catch.
   */
  const finishLogin = useCallback(
    async (tokenResponse: LoginResponse): Promise<AdminLoginResult> => {
      // Decode the JWT to get user info and pharmacy
      const token = tokenResponse.access_token
      const payload = decodeAdminJWT(token)

      if (!payload) {
        const message = 'Invalid token received'
        setError(message)
        // apiAdminLogin already stored the token — drop it rather than leaving an
        // unusable one in localStorage to be attached to later requests.
        await apiAdminLogout(pharmacySlug)
        setLoading(false)
        return { success: false, error: message }
      }

      // Construct user from JWT payload
      const adminUser: PharmacyUserResponse = {
        id: payload.sub,
        pharmacy_id: payload.pharmacy_id,
        email: payload.email,
        name: payload.name,
        role: payload.role as PharmacyUserResponse['role'],
        status: 'active',
        last_login_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }

      // Fetch pharmacy to get the slug
      const pharmacyData = await getPharmacy(pharmacySlug)

      // Validate that the user belongs to this pharmacy
      if (pharmacyData.id !== payload.pharmacy_id) {
        const message = 'Your account is not associated with this pharmacy'
        setError(message)
        await apiAdminLogout(pharmacySlug)
        setLoading(false)
        return { success: false, error: message }
      }

      setUser(adminUser)
      setPharmacy(pharmacyData)
      setLoading(false)

      // Store pharmacy slug for use in landing page
      safeSetItem(ADMIN_PHARMACY_SLUG_KEY, pharmacyData.portal_slug)

      return { success: true, pharmacySlug: pharmacyData.portal_slug }
    },
    [pharmacySlug]
  )

  /**
   * Login with email and password.
   * Returns success status and the pharmacy slug for redirect. When a code is
   * needed it returns `success: false` with no error and sets `twoFactorPending`.
   */
  const login = useCallback(
    async (
      email: string,
      password: string,
      rememberMe = false
    ): Promise<AdminLoginResult> => {
      setError(null)
      setLoading(true)

      try {
        const request: LoginRequest = {
          email,
          password,
          remember_me: rememberMe,
          // Scope the lookup to this portal: the same email can be a separate
          // account at another pharmacy, and without the slug the API refuses to
          // pick one rather than authenticating an arbitrary account.
          portal_slug: pharmacySlug,
        }

        const result = await apiAdminLogin(request)
        if (isTwoFactorChallenge(result)) {
          setTwoFactorPending({ token: result.two_factor_token, email, rememberMe })
          setLoading(false)
          return { success: false }
        }

        return await finishLogin(result)
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Login failed. Please check your credentials.'
        setError(message)
        setLoading(false)
        return { success: false, error: message }
      }
    },
    [pharmacySlug, finishLogin]
  )

  /**
   * Finish a pending 2FA login with the code from the authenticator app.
   * An expired challenge drops back to the password form.
   */
  const verifyTwoFactor = useCallback(
    async (code: string, rememberDevice: boolean): Promise<AdminLoginResult> => {
      if (!twoFactorPending) return { success: false }
      setError(null)
      setLoading(true)

      try {
        const tokenResponse = await apiAdminLoginTwoFactor(
          {
            two_factor_token: twoFactorPending.token,
            code,
            remember_device: rememberDevice,
          },
          twoFactorPending.email,
          twoFactorPending.rememberMe,
          pharmacySlug
        )
        setTwoFactorPending(null)
        return await finishLogin(tokenResponse)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Verification failed. Please try again.'
        if (message.includes('expired')) {
          setTwoFactorPending(null)
        }
        setError(message)
        setLoading(false)
        return { success: false, error: message }
      }
    },
    [twoFactorPending, pharmacySlug, finishLogin]
  )

  const cancelTwoFactor = useCallback(() => {
    setTwoFactorPending(null)
    setError(null)
  }, [])

  /**
   * Logout the current admin.
   */
  const logout = useCallback(async () => {
    setLoading(true)
    try {
      await apiAdminLogout(pharmacySlug)
    } finally {
      setUser(null)
      setPharmacy(null)
      setError(null)
      setLoading(false)
      // The landing-page hint is cleared by clearAdminAuth() only when it points
      // at this portal, so another portal's live session keeps its link.
    }
  }, [pharmacySlug])

  /**
   * Refresh the pharmacy data from the API.
   */
  const refreshPharmacy = useCallback(async () => {
    if (!isAuthenticated || !pharmacySlug) return

    try {
      const pharmacyData = await getPharmacy(pharmacySlug)
      setPharmacy(pharmacyData)
    } catch {
      // If pharmacy fetch fails, user might no longer have access
      await logout()
    }
  }, [isAuthenticated, pharmacySlug, logout])

  /**
   * Clear any auth error.
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const value: AdminAuthContextValue = {
    user,
    pharmacy,
    loading,
    isAuthenticated,
    error,
    twoFactorPending,
    login,
    verifyTwoFactor,
    cancelTwoFactor,
    logout,
    refreshPharmacy,
    clearError,
  }

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}

/**
 * Hook to access admin auth state and methods from any component.
 * Must be used within an AdminAuthProvider.
 *
 * @example
 * ```tsx
 * function AdminDashboard() {
 *   const { user, pharmacy, logout } = useAdminAuthContext()
 *
 *   return (
 *     <div>
 *       <h1>Welcome, {user?.name}</h1>
 *       <p>Managing: {pharmacy?.name}</p>
 *       <button onClick={logout}>Logout</button>
 *     </div>
 *   )
 * }
 * ```
 */
export function useAdminAuthContext(): AdminAuthContextValue {
  const context = useContext(AdminAuthContext)
  if (!context) {
    throw new Error(
      'useAdminAuthContext must be used within an AdminAuthProvider. ' +
      'Make sure AdminLayout is rendered inside AdminAuthProvider.'
    )
  }
  return context
}

/**
 * Optional hook that returns null if not inside AdminAuthProvider.
 * Use this when you need to check if the provider is available.
 */
export function useOptionalAdminAuthContext(): AdminAuthContextValue | null {
  return useContext(AdminAuthContext)
}

export default AdminAuthProvider
